import { AccommodationsSearchRequestDto } from '@/features/AccommodationList/api/requestTypes';
import { FlightsRequest } from '@/features/TransportList/api/requestTypes';
import { atom } from 'jotai';

type ChatRequestPreferences = FlightsRequest | AccommodationsSearchRequestDto;

type ChatRequests = {
  [key: string]: ChatRequestPreferences;
};

// requestId -> preference field -> timestamp of the user's last on-screen edit
type UserEdits = {
  [requestId: string]: { [field: string]: number };
};

export type ChatRequestSource =
  | { source: 'user' }
  | { source: 'agent'; sendToAgentAtTime?: number };

export const chatRequestsAtom = atom<ChatRequests>({});

const userEditsAtom = atom<UserEdits>({});

// Write-only atom: the first argument (read) is null because it holds no value
// of its own; the second (write) runs on every call. Inside it, `get` reads and
// `set` updates other atoms in one synchronous step, so the merge always sees
// the latest state. Use it with useSetAtom.
export const applyChatRequestAtom = atom(
  null,
  (
    get,
    set,
    update: {
      requestId: string;
      preferences: ChatRequestPreferences;
    } & ChatRequestSource
  ) => {
    const { requestId, preferences } = update;
    const current = get(chatRequestsAtom)[requestId] as
      | Record<string, unknown>
      | undefined;
    const incoming = preferences as unknown as Record<string, unknown>;

    if (update.source === 'user') {
      const now = Date.now();
      const changedFields = Object.keys(incoming).filter(
        key => !isSameValue(incoming[key], current?.[key])
      );
      set(userEditsAtom, prev => ({
        ...prev,
        [requestId]: {
          ...prev[requestId],
          ...Object.fromEntries(changedFields.map(key => [key, now]))
        }
      }));
      set(chatRequestsAtom, prev => ({ ...prev, [requestId]: preferences }));
      return;
    }

    // The agent can't know about on-screen edits made after its message was
    // sent, so those fields keep the user's value.
    const { sendToAgentAtTime } = update;
    const edits = get(userEditsAtom)[requestId];
    const merged = { ...incoming };
    if (current && edits && sendToAgentAtTime !== undefined) {
      Object.entries(edits).forEach(([key, editedAt]) => {
        if (editedAt > sendToAgentAtTime) {
          merged[key] = current[key];
        }
      });
    }

    set(chatRequestsAtom, prev => ({
      ...prev,
      [requestId]: merged as unknown as ChatRequestPreferences
    }));
  }
);

const isSameValue = (a: unknown, b: unknown) =>
  a === b || JSON.stringify(a) === JSON.stringify(b);
